import type { ProfileImageSpec } from 'fym-shared/src/music24/artistProfile';
import type { HeaderBox } from 'fym-shared/src/music24/spotifyHeader';

/** A box in file pixels. */
export interface PxBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** What the label of the green box says (design artist-profile `ProfileFrame`). */
export type ProfileGuideLabel = 'everyScreen' | 'subject' | 'ratio';

export interface ProfileGuides {
  /** The green box: the YouTube safe area, the subject box of Facebook, the crop of a press photo. */
  safe: PxBox | null;
  label: ProfileGuideLabel | null;
  /** The shaded bands that show only on a TV (YouTube). */
  bands: PxBox[];
  /** Where the site draws the round profile photo (Facebook, GUESS), or null. */
  sitePhoto: { left: number; top: number; size: number } | null;
}

type SpecSize = Pick<ProfileImageSpec, 'width' | 'height'>;

/** A box in shares (0 to 1) as file pixels, whole pixels. Pure. */
export function shareBoxPx(box: HeaderBox, spec: SpecSize): PxBox {
  return {
    left: Math.round(box.x0 * spec.width),
    top: Math.round(box.y0 * spec.height),
    width: Math.round((box.x1 - box.x0) * spec.width),
    height: Math.round((box.y1 - box.y0) * spec.height),
  };
}

/** The scale from the file to a preview `width` px wide, and the preview height. Pure. */
export function profilePreviewScale(spec: SpecSize, width: number): { scale: number; height: number } {
  const scale = width / spec.width;
  return { scale, height: Math.round(spec.height * scale) };
}

/** The crop guide of a press photo (design b4): a box 6 % in from each edge. */
const PRESS_CROP: HeaderBox = { x0: 0.06, x1: 0.94, y0: 0.06, y1: 0.94 };

/**
 * The round profile photo that Facebook draws over the bottom left of a banner (design
 * `ProfileFrame`, GUESS: no site publishes it): the size and the place in shares of the file.
 */
const SITE_PHOTO: Partial<Record<ProfileImageSpec['kind'], { size: number; left: number; top: number }>> = {
  facebookCover: { size: 0.19, left: 0.03, top: 0.62 },
};

/**
 * The guides of one profile image in file pixels (design b2, b4): the YouTube safe area with the
 * TV bands above and below it, the subject box and the round photo place of Facebook, the
 * crop box of a press photo. The Spotify header and the artist photo have their own preview. Pure.
 */
export function profileGuides(spec: ProfileImageSpec): ProfileGuides {
  const photo = SITE_PHOTO[spec.kind];
  const sitePhoto =
    photo === undefined
      ? null
      : {
          size: Math.round(photo.size * spec.width),
          left: Math.round(photo.left * spec.width),
          top: Math.round(photo.top * spec.height),
        };
  switch (spec.kind) {
    case 'youtubeBanner': {
      const safe = shareBoxPx(spec.subject, spec);
      return {
        safe,
        label: 'everyScreen',
        bands: [
          { left: 0, top: 0, width: spec.width, height: safe.top },
          { left: 0, top: safe.top + safe.height, width: spec.width, height: spec.height - safe.top - safe.height },
        ],
        sitePhoto: null,
      };
    }
    case 'facebookCover':
      return { safe: shareBoxPx(spec.subject, spec), label: 'subject', bands: [], sitePhoto };
    case 'pressPhoto':
      return { safe: shareBoxPx(PRESS_CROP, spec), label: 'ratio', bands: [], sitePhoto: null };
    case 'spotifyHeader':
    case 'spotifyAvatar':
      return { safe: null, label: null, bands: [], sitePhoto: null };
  }
}

/** The ratio label of a press crop ("3:2", "4:5", "1:1"). Pure. */
export function pressRatioLabel(variant: ProfileImageSpec['variant']): string {
  if (variant === 'landscape') {
    return '3:2';
  }
  if (variant === 'portrait') {
    return '4:5';
  }
  return '1:1';
}
